import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { Form, PageSection, Title, FormGroup, TextInput, ActionGroup, Button, FormAlert, Alert, } from '@patternfly/react-core';
import UserModel from '@app/Model/UserModel';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import TypeAssertions from '@app/utils/TypeAssertions';
import FieldsInterface from '@app/Interface/FieldsInterface';
import { getChangePasswordFields } from './MyAccountPage/MyAccountFunctions';
import UserAuthentication from '@app/Security/UserAuthentication';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import PasswordStrengthBar, { PasswordFeedback } from 'react-password-strength-bar';
import htmlParser from '@app/utils/htmlParser';
import Cookies from 'js-cookie';

interface ChangePasswordProps {
    pageData: PageModel,
    currentUser: UserModel
}

interface PasswordScoreInterface {
    score: number;
    feedback: PasswordFeedback;
}

const ChangePasswordPage: React.FC<ChangePasswordProps> = ({ pageData, currentUser }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const initialFieldValues: FieldsInterface = getChangePasswordFields();

    const [formFields, setFormFields] = React.useState(initialFieldValues);

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const initialPasswordStrength: PasswordScoreInterface = { score: 0, feedback: {} };

    const [passwordStrength, setPasswordStrength] = React.useState(initialPasswordStrength);

    const defaultFormValidated: "default" | "required" | "pwStrength" = "default";

    // for values: see https://www.patternfly.org/v4/components/form#formgroup
    const [formValidated, setFormValidated] = React.useState<"default" | "required" | "pwStrength">(defaultFormValidated);

    const handleChange = (newValue: string, e: React.FormEvent<HTMLInputElement>) => {

        const fieldId = e.currentTarget.id;

        const updatedFields = { ...formFields };

        updatedFields[fieldId].value = newValue;

        updatedFields[fieldId].validated = "default";

        setFormFields(updatedFields);

        setFormValidated("default");

    }

    const handleChangePasswordScore = (score: number, feedback: PasswordFeedback) => {

        const newScore: PasswordScoreInterface = {
            score: score,
            feedback: feedback
        };

        setPasswordStrength(newScore);

    }

    const handleSubmit = async (e: React.SyntheticEvent) => {

        e.preventDefault();

        setIsFormProcessing(true);

        const validform = await formValidation();

        if (validform) {

            const newPassword = String(formFields["newPassword"].value);

            const responseCode = await UserAuthentication.saveUserPassword(currentUser, newPassword);

            let notificationType: ToastNotificationType = ToastNotificationType.Default;

            let toastTitle = "";

            let toastDescription = "";

            const updatedFields = { ...formFields };

            switch (responseCode) {

                case 200:
                case 201:
                    notificationType = ToastNotificationType.Success;
                    toastTitle = "Success";
                    toastDescription = "Password was successfully saved.";

                    // clear form values
                    updatedFields["originalPassword"].value = "";
                    updatedFields["newPassword"].value = "";
                    updatedFields["confirmPassword"].value = "";

                    setFormFields(updatedFields);

                    break;

                case 400:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Invalid input";
                    toastDescription = "Please try again or contact support if the issue persists.";
                    break;

                case 404:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Resource not found";
                    toastDescription = "Endpoint to save password is invalid, try again or contact support.";
                    break;

                case 422:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Bad user data";
                    toastDescription = "Updated data error";
                    break;

                default:
                    notificationType = ToastNotificationType.Warning;
                    toastTitle = "Unknown error";
                    toastDescription = "Unable to save password, contact support.";
                    break;

            }

            CreateToastNotification(toastDispatch, notificationType, toastTitle, toastDescription);

        }

        setIsFormProcessing(false);

    }

    const isOldPasswordGood = async (oldPassword: string): Promise<boolean> => {
        let isGood;
        const twoFaExpireDate = Cookies.get('2fa-remember-device');
        try {
            isGood = await UserAuthentication.signIn(currentUser.username, oldPassword, false, twoFaExpireDate);
        } catch (error) {
            /*
                TODO: There's an edge case here where the password is good but 2FA has expired.
                Should probably rethink how we check existing passwords as part of the reset workflow.
            */
            isGood = false;
        }

        return !!isGood;
    }

    /**
     * Validates form fields
     * @returns boolean
     */
    const formValidation = async (): Promise<boolean> => {

        let validForm = true;

        const updatedFields = { ...formFields };

        setFormValidated("default");

        let oldPassword = "";

        // narrow password to a string
        if (typeof formFields["originalPassword"].value === "string") {
            oldPassword = formFields["originalPassword"].value;
        }

        let newPassword = "";

        // narrow password to a string
        if (typeof formFields["newPassword"].value === "string") {
            newPassword = formFields["newPassword"].value;
        }

        // Old Password
        const originalPassword = String(formFields["originalPassword"].value);
        if (TypeAssertions.isStringNullOrEmpty(originalPassword)) {

            // required
            setFormValidated("required");

            validForm = false;

        } else if (await isOldPasswordGood(oldPassword) === false) {

            // old password must be good
            updatedFields["originalPassword"].validated = "error";

            validForm = false;

        } else {

            updatedFields["originalPassword"].validated = "default";
        }

        // newPassword
        if (TypeAssertions.isStringNullOrEmpty(newPassword) || TypeAssertions.isSecurePassword(newPassword) !== "good") {

            // required
            setFormValidated("required");

            updatedFields["newPassword"].validated = "error";

            validForm = false;

        } else if (passwordStrength.score < 3) {

            setFormValidated("pwStrength");

            // confirm password strength
            // https://github.com/dropbox/zxcvbn
            //     result.score   # Integer from 0-4. 0,1,2 throw error. 3,4 OK to use.
            updatedFields["newPassword"].validated = "error";

            validForm = false;

        } else {

            updatedFields["newPassword"].validated = "default";

        }

        // confirmPassword
        const confirmPassword = String(formFields["confirmPassword"].value);
        if (TypeAssertions.isStringNullOrEmpty(confirmPassword)) {

            // required
            setFormValidated("required");

            validForm = false;

        } else if (formFields["newPassword"].value != formFields["confirmPassword"].value) {

            // passwords must match
            updatedFields["confirmPassword"].validated = "error";

            validForm = false;

        } else {

            updatedFields["confirmPassword"].validated = "default";

        }

        setFormFields(updatedFields);

        return validForm;

    }

    React.useEffect(() => {

        return () => {

            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);

    let formAlert = (<></>);

    if (formValidated === "pwStrength" && passwordStrength.score < 3) {

        formAlert = (<FormAlert>
            <Alert
                variant="danger"
                title={passwordStrength.feedback.warning}
                aria-live="polite"
                role="alert"
                isInline
            >
                {passwordStrength.feedback.suggestions?.map(((suggestion, index) => (
                    <span key={index}>{suggestion}</span>
                )))}
            </Alert>
        </FormAlert>);

    } else if (formValidated === "required") {

        formAlert = (<FormAlert>
            <Alert
                variant="danger"
                title="Fill out all required fields before continuing"
                aria-live="polite"
                role="alert"
                isInline
            ></Alert>
        </FormAlert>);
    }

    return (

        <PageSection>
            <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
            <div className="pf-c-content">
                {htmlParser.htmlToJSX(pageData.content ?? '')}
            </div>
            <Form autoComplete="false" isWidthLimited>
                {formAlert}
                <FormGroup
                    label={formFields["originalPassword"].label}
                    isRequired={formFields["originalPassword"].required}
                    fieldId={formFields["originalPassword"].fieldId}
                    helperTextInvalid={formFields["originalPassword"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["originalPassword"].validated}
                >
                    <TextInput
                        isRequired={formFields["originalPassword"].required}
                        type="password"
                        id={formFields["originalPassword"].fieldId}
                        name={formFields["originalPassword"].fieldId}
                        value={typeof formFields["originalPassword"].value === "string" ? formFields["originalPassword"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>
                <FormGroup
                    label={formFields["newPassword"].label}
                    isRequired={formFields["newPassword"].required}
                    fieldId={formFields["newPassword"].fieldId}
                    helperTextInvalid={formFields["newPassword"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["newPassword"].validated}
                >
                    <TextInput
                        isRequired={formFields["newPassword"].required}
                        type="password"
                        id={formFields["newPassword"].fieldId}
                        name={formFields["newPassword"].fieldId}
                        value={typeof formFields["newPassword"].value === "string" ? formFields["newPassword"].value : ""}
                        onChange={handleChange}
                    />
                    <PasswordStrengthBar scoreWordStyle={{color: '#000000'}} minLength={6} password={String(formFields["newPassword"].value)} onChangeScore={handleChangePasswordScore} />
                </FormGroup>
                <FormGroup
                    label={formFields["confirmPassword"].label}
                    isRequired={formFields["confirmPassword"].required}
                    fieldId={formFields["confirmPassword"].fieldId}
                    helperTextInvalid={formFields["confirmPassword"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["confirmPassword"].validated}
                >
                    <TextInput
                        isRequired={formFields["confirmPassword"].required}
                        type="password"
                        id={formFields["confirmPassword"].fieldId}
                        name={formFields["confirmPassword"].fieldId}
                        value={typeof formFields["confirmPassword"].value === "string" ? formFields["confirmPassword"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>
                <ActionGroup>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        spinnerAriaValueText={isFormProcessing ? "Saving" : undefined}
                        isLoading={isFormProcessing}
                    >{isFormProcessing ? "Saving" : "Submit"}</Button>
                </ActionGroup>
            </Form>
        </PageSection>

    )

};

export default ChangePasswordPage;